import { createApi } from '@reduxjs/toolkit/query/react';

import { PaginationMeta } from 'src/global/schemas/analytics.schema';
import {
  ChartDataResponse,
  FundingDetailsResponse,
  FundingDistributionResponse,
  ProjectDashboardPeriodType,
  ProjectOverviewResponse,
  RolesResponse,
} from 'src/global/schemas/project-dashboard.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

// Helper function to build headers for project dashboard requests
const buildProjectDashboardHeaders = (address?: string, network?: string) => ({
  ...(address && { 'x-offchain-address': address }),
  ...(network && { 'x-offchain-network': network }),
});

interface ProjectDashboardParams {
  projectId: string;
  address?: string;
  network?: string;
}

interface ProjectChartParams extends ProjectDashboardParams {
  period?: ProjectDashboardPeriodType;
  page?: number;
  size?: number;
  from?: string;
  to?: string;
  search?: string;
  sort?: string;
  filter?: string;
}

interface ProjectPaginatedParams extends ProjectDashboardParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  filter?: string;
}

export const projectDashboardApi = createApi({
  reducerPath: REDUX_SLICE.PROJECT_DASHBOARD_API,
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    // Project Overview endpoint
    getProjectOverview: builder.query<ProjectOverviewResponse['data'], ProjectDashboardParams>({
      query: ({ projectId, address, network }) => ({
        method: 'GET',
        path: `/v1/projects/${projectId}/summaries/overview`,
        headers: buildProjectDashboardHeaders(address, network),
      }),
      transformResponse: (returnValue: ProjectOverviewResponse) => returnValue.data,
    }),

    // Total Revenues chart endpoint
    getProjectTotalRevenues: builder.query<ChartDataResponse['data'], ProjectChartParams>({
      query: params => {
        const { projectId, address, network, ...queryParams } = params;
        const urlParams = new URLSearchParams();
        if (queryParams.period) urlParams.append('period', queryParams.period);
        if (queryParams.page) urlParams.append('page', queryParams.page.toString());
        if (queryParams.size) urlParams.append('size', queryParams.size.toString());
        if (queryParams.from) urlParams.append('from', queryParams.from);
        if (queryParams.to) urlParams.append('to', queryParams.to);
        if (queryParams.search) urlParams.append('search', queryParams.search);
        if (queryParams.sort) urlParams.append('sort', queryParams.sort);
        if (queryParams.filter) urlParams.append('filter', queryParams.filter);

        return {
          method: 'GET',
          path: `/v1/projects/${projectId}/summaries/total-revenues?${urlParams.toString()}`,
          headers: buildProjectDashboardHeaders(address, network),
        };
      },
      transformResponse: (returnValue: ChartDataResponse) => returnValue.data,
    }),

    // Funding Details endpoint (paginated)
    getProjectFundingDetails: builder.query<
      { data: FundingDetailsResponse['data']; meta: PaginationMeta },
      ProjectPaginatedParams
    >({
      query: params => {
        const { projectId, address, network, ...queryParams } = params;
        const urlParams = new URLSearchParams();
        if (queryParams.page) urlParams.append('page', queryParams.page.toString());
        if (queryParams.size) urlParams.append('size', queryParams.size.toString());
        if (queryParams.search) urlParams.append('search', queryParams.search);
        if (queryParams.sort) urlParams.append('sort', queryParams.sort);
        if (queryParams.filter) urlParams.append('filter', queryParams.filter);

        return {
          method: 'GET',
          path: `/v1/projects/${projectId}/summaries/funding-details?${urlParams.toString()}`,
          headers: buildProjectDashboardHeaders(address, network),
        };
      },
      transformResponse: (returnValue: FundingDetailsResponse & { meta: PaginationMeta }) => ({
        data: returnValue.data,
        meta: returnValue.meta,
      }),
    }),

    // Funding Distributions endpoint
    getProjectFundingDistributions: builder.query<
      { status: number; code: number; data: FundingDistributionResponse['data'] },
      ProjectDashboardParams
    >({
      query: ({ projectId, address, network }) => ({
        method: 'GET',
        path: `/v1/projects/${projectId}/summaries/funding-distributions`,
        headers: buildProjectDashboardHeaders(address, network),
      }),
      transformResponse: (returnValue: FundingDistributionResponse) => ({
        status: returnValue.status as unknown as number,
        code: 200,
        data: returnValue.data ?? {},
      }),
    }),

    // LFG Revenues chart endpoint
    getProjectLfgRevenues: builder.query<ChartDataResponse['data'], ProjectChartParams>({
      query: params => {
        const { projectId, address, network, ...queryParams } = params;
        const urlParams = new URLSearchParams();
        if (queryParams.period) urlParams.append('period', queryParams.period);
        if (queryParams.page) urlParams.append('page', queryParams.page.toString());
        if (queryParams.size) urlParams.append('size', queryParams.size.toString());
        if (queryParams.from) urlParams.append('from', queryParams.from);
        if (queryParams.to) urlParams.append('to', queryParams.to);
        if (queryParams.search) urlParams.append('search', queryParams.search);
        if (queryParams.sort) urlParams.append('sort', queryParams.sort);
        if (queryParams.filter) urlParams.append('filter', queryParams.filter);

        return {
          method: 'GET',
          path: `/v1/projects/${projectId}/summaries/lfg-revenues?${urlParams.toString()}`,
          headers: buildProjectDashboardHeaders(address, network),
        };
      },
      transformResponse: (returnValue: ChartDataResponse) => returnValue.data,
    }),

    // Roles endpoint (paginated)
    getProjectRoles: builder.query<{ data: RolesResponse['data']; meta: PaginationMeta }, ProjectPaginatedParams>({
      query: params => {
        const { projectId, address, network, ...queryParams } = params;
        const urlParams = new URLSearchParams();
        if (queryParams.page) urlParams.append('page', queryParams.page.toString());
        if (queryParams.size) urlParams.append('size', queryParams.size.toString());
        if (queryParams.search) urlParams.append('search', queryParams.search);
        if (queryParams.sort) urlParams.append('sort', queryParams.sort);
        if (queryParams.filter) urlParams.append('filter', queryParams.filter);

        return {
          method: 'GET',
          path: `/v1/projects/${projectId}/summaries/roles?${urlParams.toString()}`,
          headers: buildProjectDashboardHeaders(address, network),
        };
      },
      transformResponse: (returnValue: RolesResponse & { meta: PaginationMeta }) => ({
        data: returnValue.data,
        meta: returnValue.meta,
      }),
    }),

    // OTC Node Sold chart endpoint
    getProjectOtcNodeSold: builder.query<ChartDataResponse['data'], ProjectChartParams>({
      query: params => {
        const { projectId, address, network, ...queryParams } = params;
        const urlParams = new URLSearchParams();
        if (queryParams.period) urlParams.append('period', queryParams.period);
        if (queryParams.page) urlParams.append('page', queryParams.page.toString());
        if (queryParams.size) urlParams.append('size', queryParams.size.toString());
        if (queryParams.from) urlParams.append('from', queryParams.from);
        if (queryParams.to) urlParams.append('to', queryParams.to);
        if (queryParams.search) urlParams.append('search', queryParams.search);
        if (queryParams.sort) urlParams.append('sort', queryParams.sort);
        if (queryParams.filter) urlParams.append('filter', queryParams.filter);

        return {
          method: 'GET',
          path: `/v1/projects/${projectId}/summaries/otc-node-sold?${urlParams.toString()}`,
          headers: buildProjectDashboardHeaders(address, network),
        };
      },
      transformResponse: (returnValue: ChartDataResponse) => returnValue.data,
    }),

    // OTC Revenues chart endpoint
    getProjectOtcRevenues: builder.query<ChartDataResponse['data'], ProjectChartParams>({
      query: params => {
        const { projectId, address, network, ...queryParams } = params;
        const urlParams = new URLSearchParams();
        if (queryParams.period) urlParams.append('period', queryParams.period);
        if (queryParams.page) urlParams.append('page', queryParams.page.toString());
        if (queryParams.size) urlParams.append('size', queryParams.size.toString());
        if (queryParams.from) urlParams.append('from', queryParams.from);
        if (queryParams.to) urlParams.append('to', queryParams.to);
        if (queryParams.search) urlParams.append('search', queryParams.search);
        if (queryParams.sort) urlParams.append('sort', queryParams.sort);
        if (queryParams.filter) urlParams.append('filter', queryParams.filter);

        return {
          method: 'GET',
          path: `/v1/projects/${projectId}/summaries/otc-revenues?${urlParams.toString()}`,
          headers: buildProjectDashboardHeaders(address, network),
        };
      },
      transformResponse: (returnValue: ChartDataResponse) => returnValue.data,
    }),
  }),
});

export const {
  useGetProjectOverviewQuery,
  useGetProjectTotalRevenuesQuery,
  useGetProjectFundingDetailsQuery,
  useGetProjectFundingDistributionsQuery,
  useGetProjectLfgRevenuesQuery,
  useGetProjectRolesQuery,
  useGetProjectOtcNodeSoldQuery,
  useGetProjectOtcRevenuesQuery,
} = projectDashboardApi;
